import { useMyTimesheets } from "@/features/timesheets/model/useTimesheets";
import type { Timesheet } from "@/features/timesheets/types";
import { formatIsoTime12h } from "@/shared/lib/time";
import { TimesheetFlagBadges, TimesheetStatusBadge } from "@/features/timesheets/ui/TimesheetStatusBadge";

interface Props {
  shiftId: string;
}

/**
 * Inline timesheet summary for the worker viewing one of their completed shifts.
 * Renders nothing if there's no timesheet yet (e.g., the worker is still on
 * the shift). When disputed, shows the venue's adjusted hours / pay / note.
 */
export function WorkerTimesheetCard({ shiftId }: Props) {
  const { data: timesheets } = useMyTimesheets();
  const timesheet: Timesheet | undefined = timesheets?.find((t) => t.shift_id === shiftId);
  if (!timesheet) return null;

  return (
    <div className="rounded-lg border border-border bg-surface p-4 shadow-card space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-copy">Your timesheet</h3>
        <TimesheetStatusBadge
          status={timesheet.status}
          reviewedBy={timesheet.reviewed_by}
        />
      </div>

      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-copy-muted">Checked in</dt>
          <dd className="text-copy">{formatIsoTime12h(timesheet.check_in_at)}</dd>
        </div>
        <div>
          <dt className="text-copy-muted">Checked out</dt>
          <dd className="text-copy">{formatIsoTime12h(timesheet.check_out_at)}</dd>
        </div>
        <div>
          <dt className="text-copy-muted">Hours</dt>
          <dd className="font-medium tabular-nums text-copy">{timesheet.hours_worked}h</dd>
        </div>
        <div>
          <dt className="text-copy-muted">Pay</dt>
          <dd className="font-medium tabular-nums text-copy">${timesheet.gross_pay}</dd>
        </div>
      </dl>

      <TimesheetFlagBadges timesheet={timesheet} />

      {timesheet.status === "DISPUTED" && (
        <div className="rounded-md border border-destructive bg-destructive-light p-3 text-sm">
          <p className="font-medium text-destructive-foreground">
            Venue adjusted hours to {timesheet.adjusted_hours}h (${timesheet.adjusted_pay})
          </p>
          {timesheet.venue_notes && (
            <p className="mt-1 text-destructive-foreground/90">{timesheet.venue_notes}</p>
          )}
          <p className="mt-2 text-xs text-copy-muted">
            If you disagree, contact OneShift support.
          </p>
        </div>
      )}
    </div>
  );
}
